import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useParams } from 'react-router-dom';
import { Input, Select, Checkbox } from 'antd';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { isOwn } from './mockData';
import { usePlatformState } from '../../../features/platform/platformStateSlice';
import {
  useGetUnitsQuery,
  useGetStateDepartmentQuery,
  useCreateUnitMutation,
  useSetLevelRuleMutation,
  useUpdateUnitMutation,
  useDeleteUnitMutation,
  useImportUnitsMutation,
  useLazyGetUnitsTemplateQuery,
  useGetLevelNodesQuery,
  useGetUnitMembersQuery,
  useSaveUnitMembersMutation,
  type Nesting,
  type AvailableNode,
  type UnitMember,
  type UnitsImportResult,
} from '../../../features/platform/platformApi';

/** Stable empty default so `currentMembers` keeps the same reference between renders
 *  (a fresh `[]` each render would retrigger the members effect → infinite re-render loop). */
const EMPTY_MEMBERS: UnitMember[] = [];

/* ------------------------------------------------------------------ *
 * Platform Admin — Units editor (prototype `isUnits`).
 * Define the units of one Own-Grouped level: nesting rule, units list,
 * and each unit's members — the real LGD/ECI/SEC nodes of the level
 * below, via the node-mapping API.
 * Route: /admin/departments/:code/units/:level
 * ------------------------------------------------------------------ */

const NESTING_OPTS: { value: Nesting; label: string }[] = [
  { value: 'within', label: 'Must sit inside one parent' },
  { value: 'derive', label: 'Parent worked out from geography' },
  { value: 'free', label: 'Parent chosen by hand (may cross boundaries)' },
];

export default function UnitsEditorPage() {
  const { code, level } = useParams<{ code: string; level: string }>();
  const { code: stateCode, name: stateName } = usePlatformState();
  const templateCode = code ?? '';
  const levelName = level ? decodeURIComponent(level) : 'Level';

  const { data: dept } = useGetStateDepartmentQuery(
    { code: stateCode, tcode: templateCode },
    { skip: !stateCode || !templateCode },
  );
  const deptName = dept?.name ?? 'Department';

  // The level below this one supplies the source nodes each unit is composed of.
  const levels = useMemo(() => dept?.levels ?? [], [dept]);
  const currentLevel = useMemo(() => levels.find((l) => l.name === levelName), [levels, levelName]);
  // Own levels have hand-entered units (add/rename/delete). Inherited levels list their real source
  // nodes (e.g. an Inherited District → the ECI districts) but each is still mappable like an Own
  // unit: picking one find-or-creates its TerritoryUnit (by ECI code) so members can be saved.
  // Anchored levels are purely read-only source nodes.
  const isOwnLevel = currentLevel ? isOwn(currentLevel.binding) : false;
  const mapInherited = currentLevel?.binding === 'inherited';
  const isAnchorLevel = currentLevel?.binding === 'anchor';
  // `canMapMembers` / `mapSource` are finalised below once the source nodes are known (a non-state
  // anchored root has many nodes and becomes mappable like an inherited level).
  const lowerLevel = useMemo(() => {
    const i = levels.findIndex((l) => l.name === levelName);
    return i >= 0 ? levels[i + 1] : undefined;
  }, [levels, levelName]);
  const { data, isLoading } = useGetUnitsQuery(
    { code: stateCode, template_code: templateCode, level: levelName },
    { skip: !stateCode || !templateCode },
  );
  const units = useMemo(() => data?.units ?? [], [data]);

  const [createUnit] = useCreateUnitMutation();
  const [setLevelRule] = useSetLevelRuleMutation();
  const [updateUnit] = useUpdateUnitMutation();
  const [deleteUnit] = useDeleteUnitMutation();
  const [saveUnitMembers] = useSaveUnitMembersMutation();
  const [fetchUnitsTemplate] = useLazyGetUnitsTemplateQuery();

  const [nesting, setNesting] = useState<Nesting>('derive');
  const [selId, setSelId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [nodeSearch, setNodeSearch] = useState('');
  const [nodeSearchQ, setNodeSearchQ] = useState('');
  const [onlyFree, setOnlyFree] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [saveFlash, setSaveFlash] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importUnits, { isLoading: importing }] = useImportUnitsMutation();
  const [importResult, setImportResult] = useState<UnitsImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const onFilePicked = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = ''; // allow picking the same file again
    if (!f) return;
    setImportResult(null);
    setImportError(null);
    try {
      const res = await importUnits({
        code: stateCode,
        template_code: templateCode,
        level: levelName,
        file: f,
      }).unwrap();
      setImportResult(res);
    } catch (err) {
      const detail = (err as { data?: { detail?: string } })?.data?.detail;
      setImportError(detail || 'Could not import the file.');
    }
  };
  // The current unit's chosen nodes, keyed by node_code (editable before Save).
  const [selectedNodes, setSelectedNodes] = useState<Record<string, AvailableNode>>({});

  useEffect(() => {
    if (data?.nesting) setNesting(data.nesting);
  }, [data?.nesting]);

  const sel = units.find((u) => u.id === selId) ?? null;
  useEffect(() => {
    if (sel) setDraftName(sel.name);
  }, [sel]);

  // Debounce the node search box.
  useEffect(() => {
    const t = window.setTimeout(() => setNodeSearchQ(nodeSearch.trim()), 300);
    return () => window.clearTimeout(t);
  }, [nodeSearch]);

  // Debounce the unit/node list search box.
  const [listSearchQ, setListSearchQ] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setListSearchQ(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  // Anchored/Inherited current level: list its real source nodes (read-only), resolved by the
  // mapped source_level — not guessed from the level name.
  const { data: levelNodes, isFetching: levelNodesLoading } = useGetLevelNodesQuery(
    { code: stateCode, template_code: templateCode, level: levelName, search: listSearchQ || undefined },
    { skip: !stateCode || !templateCode || isOwnLevel },
  );
  const sourceNodes = levelNodes?.nodes ?? [];
  // A non-state anchored root (e.g. Panchayati Raj / Urban Local Body) resolves to many source
  // nodes — treat it like an inherited level so each node can be opened and mapped to the level
  // below. A single-node State root stays read-only. selectSourceNode find-or-creates the unit.
  const mapAnchorRoot = isAnchorLevel && sourceNodes.length > 1;
  const mapSource = mapInherited || mapAnchorRoot;
  const canMapMembers = isOwnLevel || mapSource;

  // Inherited: selecting a source node (district) maps to its TerritoryUnit — find it by ECI code,
  // creating it on demand — so the normal Own member-mapping flow applies.
  const selectSourceNode = async (n: AvailableNode) => {
    const existing = units.find((u) => (u.code ?? '') === n.node_code);
    if (existing) {
      setSelId(existing.id);
      return;
    }
    try {
      const created = await createUnit({
        code: stateCode,
        template_code: templateCode,
        level: levelName,
        name: n.node_name,
        unitCode: n.node_code,
      }).unwrap();
      setSelId(created.id);
    } catch {
      /* surfaced elsewhere */
    }
  };
  // The unit currently backing the selected inherited node (so its row shows as selected).
  const selNodeCode = sel?.code ?? null;

  // The selected unit's saved members → pre-tick them.
  const { data: membersData } = useGetUnitMembersQuery(
    { code: stateCode, unitId: sel?.id ?? 0 },
    { skip: !stateCode || !sel },
  );
  const currentMembers = membersData ?? EMPTY_MEMBERS;
  useEffect(() => {
    const map: Record<string, AvailableNode> = {};
    currentMembers.forEach((m) => {
      map[m.node_code] = { source: m.source, node_level: m.node_level, node_code: m.node_code, node_name: m.node_name };
    });
    setSelectedNodes(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMembers, selId]);

  // The CHILD level just below this one supplies each unit's members. Its pick-list comes from
  // level-nodes and works for ANY child binding: Own → the child's own units, Anchored/Inherited
  // → the child's source nodes. Chosen nodes are saved the usual way (node_code = child unit id
  // for Own children, or the source node_code for anchored).
  const lowerName = lowerLevel?.name || currentMembers[0]?.node_level || '';
  const { data: childNodes, isFetching: nodesLoading } = useGetLevelNodesQuery(
    {
      code: stateCode,
      template_code: templateCode,
      level: lowerName,
      search: nodeSearchQ || undefined,
      // "Only show ones not in another unit" — the backend hides child nodes/units already taken
      // by another unit of THIS level; the current unit (excludeUnit) keeps its own members.
      onlyUnassigned: onlyFree,
      unitLevel: levelName,
      excludeUnit: sel?.id,
    },
    { skip: !sel || !lowerName || !canMapMembers },
  );
  const availNodes = childNodes?.nodes ?? [];
  const lowerSource = childNodes?.source || currentMembers[0]?.source || lowerLevel?.source || '';
  // Own children report source "own" — a real source system (ECI/SEC/LGD) is worth showing; "own" isn't.
  const lowerSourceLabel = lowerSource && lowerSource.toLowerCase() !== 'own' ? lowerSource : '';
  // Any level that has a child level below it can be mapped to that child.
  const canMap = Boolean(lowerLevel);

  const selectedList = Object.values(selectedNodes);
  const selectedCount = selectedList.length;
  const plural = (n: string) => (n.toLowerCase().endsWith('s') ? n : `${n}s`);

  const stats = [
    {
      label: isOwnLevel ? 'Units' : 'Nodes',
      value: String(isOwnLevel ? units.length : sourceNodes.length),
      cls: 'text-ink',
    },
    {
      label: 'Coverage',
      value: data?.coverage == null ? '—' : `${data.coverage}%`,
      cls: data?.coverage == null ? 'text-ink-secondary' : data.coverage >= 70 ? 'text-[#8a4b0f]' : 'text-[#b42318]',
    },
  ];

  const changeNesting = async (v: Nesting) => {
    setNesting(v);
    try {
      await setLevelRule({ code: stateCode, template_code: templateCode, level: levelName, nesting: v }).unwrap();
    } catch {
      /* surfaced elsewhere */
    }
  };

  const addUnit = async () => {
    const name = search.trim();
    if (!name) return; // never create a nameless unit
    try {
      const created = await createUnit({
        code: stateCode,
        template_code: templateCode,
        level: levelName,
        name,
      }).unwrap();
      setSelId(created.id);
      setSearch('');
    } catch {
      /* surfaced elsewhere */
    }
  };

  const bulkLines = [
    'unit_name, member_type, member_name_or_code',
    'Rajgarh SD, village, Bhiwadi',
    'Rajgarh SD, village, 081234',
    'Alwar SD, block, Rajgarh',
  ];
  // Download the real .xlsx template from the API (member_type pre-filled for this level).
  const downloadTemplate = async () => {
    try {
      const blob = await fetchUnitsTemplate({
        template_code: templateCode,
        level: levelName,
        state: stateCode,
      }).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${templateCode}-${levelName}-units-template.xlsx`.replace(/\s+/g, '-');
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setImportError('Could not download the template.');
    }
  };

  const saveName = async () => {
    if (!sel || draftName === sel.name) return;
    try {
      await updateUnit({ code: stateCode, id: sel.id, patch: { name: draftName } }).unwrap();
    } catch {
      /* surfaced elsewhere */
    }
  };

  // Save the unit: persist the (possibly edited) name and replace its mapped members.
  const saveUnit = async () => {
    if (!sel) return;
    setSaving(true);
    try {
      if (draftName.trim() && draftName !== sel.name) {
        await updateUnit({ code: stateCode, id: sel.id, patch: { name: draftName.trim() } }).unwrap();
      }
      if (canMap) {
        await saveUnitMembers({ code: stateCode, unitId: sel.id, nodes: Object.values(selectedNodes) }).unwrap();
      }
      setSaveFlash(true);
      window.setTimeout(() => setSaveFlash(false), 1800);
    } catch {
      /* surfaced elsewhere */
    } finally {
      setSaving(false);
    }
  };

  const removeSel = async () => {
    if (!sel) return;
    try {
      await deleteUnit({ code: stateCode, id: sel.id }).unwrap();
      setSelId(null);
    } catch {
      /* surfaced elsewhere */
    }
  };

  const toggleNode = (n: AvailableNode) =>
    setSelectedNodes((prev) => {
      const next = { ...prev };
      if (next[n.node_code]) delete next[n.node_code];
      else next[n.node_code] = n;
      return next;
    });
  const allShownOn = availNodes.length > 0 && availNodes.every((n) => selectedNodes[n.node_code]);
  const toggleAllShown = () =>
    setSelectedNodes((prev) => {
      const next = { ...prev };
      if (allShownOn) availNodes.forEach((n) => delete next[n.node_code]);
      else availNodes.forEach((n) => (next[n.node_code] = n));
      return next;
    });

  const visibleUnits = units.filter(
    (u) => !search.trim() || u.name.toLowerCase().includes(search.trim().toLowerCase()),
  );

  return (
    <div className="flex flex-col gap-[18px]">
      <Breadcrumbs
        items={[
          { label: 'All states', to: '/admin/overview' },
          { label: `Departments in ${stateName}`, to: '/admin/departments' },
          { label: deptName, to: `/admin/departments/${templateCode}` },
          { label: `Units of ${levelName}` },
        ]}
      />

      {/* Header + stats */}
      <section className="flex flex-wrap items-start justify-between gap-x-8 gap-y-4 rounded-xl border border-line/30 bg-white p-6 shadow-card">
        <div className="min-w-0 flex-1 basis-[420px]">
          <div className="text-xs font-semibold text-brand">TERRITORIES · {deptName}</div>
          <h1 className="mb-1.5 mt-1 text-2xl font-bold text-ink">Units of {levelName}</h1>
          <p className="max-w-[620px] text-sm text-ink-secondary">
            {!canMapMembers
              ? `${levelName} is anchored — its units are the ${levelNodes?.node_level || 'source'} nodes` +
                (levelNodes?.source
                  ? ` from ${levelNodes.source}${levelNodes.source_level ? ` › ${levelNodes.source_level}` : ''}`
                  : '') +
                ' (read-only).'
              : canMap
                ? `${mapSource ? `Units are the ${levelNodes?.source || 'source'} ${plural(levelName).toLowerCase()}. ` : ''}Each unit is composed of ${lowerName || 'the level below'} nodes${lowerSourceLabel ? ` from ${lowerSourceLabel}` : ''}. Select a unit and tick the nodes that belong to it.`
                : `${levelName} has no level below it, so there’s nothing to map here.`}
          </p>
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <label className="text-xs font-semibold text-ink-secondary">Nesting rule</label>
            <Select value={nesting} onChange={changeNesting} options={NESTING_OPTS} style={{ minWidth: 280 }} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2.5">
          {stats.map((s) => (
            <div key={s.label} className="min-w-[100px] rounded-lg border border-line/50 px-3.5 py-2">
              <div className="text-xs text-ink-secondary">{s.label}</div>
              <div className={['text-[22px] font-bold', s.cls].join(' ')}>{s.value}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Bulk import — revealed by the "Import" button (Own levels only) */}
      {showBulk && isOwnLevel && (
        <section className="flex flex-col gap-3 rounded-xl border border-line/30 bg-white p-6 shadow-card">
          <h2 className="text-lg font-bold text-ink">Bulk import</h2>
          <p className="max-w-[900px] text-sm text-ink-secondary">
            A CSV or Excel file, one row per member. Rows with the same unit name fold into one unit.
            <b className="text-ink"> member_type</b> is the level below (village, block, ward, gram
            panchayat, ulb, district, assembly, parliament, booth) — leave blank to use the level just
            below {levelName}. The member is matched by code first, then by name; unmatched rows are
            reported and not saved.
          </p>
          <pre className="overflow-x-auto rounded-lg bg-[#f9fafb] p-3.5 text-[13px] leading-6 text-ink-secondary">{bulkLines.join('\n')}</pre>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={downloadTemplate}
              className="h-11 rounded-lg border border-brand bg-white px-4 text-[13px] font-semibold text-brand transition-colors hover:bg-brand-shade"
            >
              Download Excel template
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx"
              className="hidden"
              onChange={onFilePicked}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="h-11 rounded-lg bg-brand px-4 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
            >
              {importing ? 'Importing…' : 'Import from Excel or CSV'}
            </button>
          </div>

          {importError && (
            <div className="rounded-lg border border-[#fecaca] bg-[#fef2f2] p-3 text-[13px] font-semibold text-[#b42318]">
              {importError}
            </div>
          )}
          {importResult && (
            <div className="flex flex-col gap-2 rounded-lg border border-line/50 bg-[#f9fafb] p-3.5 text-[13px] text-ink">
              <span className="font-semibold text-ink">
                Imported {importResult.rows} {importResult.rows === 1 ? 'row' : 'rows'}: {importResult.units_created} unit
                {importResult.units_created === 1 ? '' : 's'} created
                {importResult.units_existing ? `, ${importResult.units_existing} reused` : ''}, {importResult.members_added}{' '}
                member{importResult.members_added === 1 ? '' : 's'} added.
              </span>
              {importResult.unmatched.length > 0 && (
                <div className="flex flex-col gap-1">
                  <span className="font-semibold text-[#b45309]">
                    {importResult.unmatched.length} row{importResult.unmatched.length === 1 ? '' : 's'} not matched (not
                    saved):
                  </span>
                  <div className="max-h-48 overflow-y-auto rounded-md border border-line/50 bg-white">
                    {importResult.unmatched.map((u, i) => (
                      <div key={i} className="border-t border-[#f3f4f6] px-2.5 py-1.5 text-xs text-ink-secondary first:border-t-0">
                        <b className="text-ink">Row {u.row}</b> · {u.unit} · {u.member_type} · “{u.value}” — {u.reason}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* Two-column: unit list + member chooser */}
      <div className="flex flex-col gap-[18px] lg:flex-row">
        {/* Unit list */}
        <section className="flex w-full flex-col gap-2.5 rounded-xl border border-line/30 bg-white p-4 shadow-card lg:w-[320px] lg:shrink-0">
          <label className="text-xs font-semibold text-ink-secondary">
            {isOwnLevel ? 'Search units / name a new one' : 'Search units'}
          </label>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isOwnLevel ? 'Type a unit name' : 'Name'}
            size="large"
            onPressEnter={isOwnLevel ? addUnit : undefined}
          />
          {isOwnLevel && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={addUnit}
                disabled={!search.trim()}
                title={!search.trim() ? 'Type a name above to add a unit' : undefined}
                className="h-10 rounded-lg bg-brand px-3.5 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50"
              >
                + Add unit
              </button>
              <button
                type="button"
                onClick={() => setShowBulk((v) => !v)}
                className={['h-10 rounded-lg border border-brand px-3.5 text-[13px] font-semibold text-brand transition-colors hover:bg-brand-shade disabled:cursor-not-allowed disabled:opacity-50', showBulk ? 'bg-brand-shade' : 'bg-white'].join(' ')}
              >
                Import
              </button>
            </div>
          )}

          <div className="flex flex-col">
            {isOwnLevel ? (
              <>
                {isLoading && <span className="border-t border-[#f3f4f6] pt-2.5 text-[13px] text-ink-secondary">Loading units…</span>}
                {!isLoading &&
                  visibleUnits.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => setSelId(u.id)}
                      className={['flex flex-col items-start gap-0.5 border-t border-[#f3f4f6] px-2 py-2.5 text-left transition-colors first:border-t-0', u.id === selId ? 'rounded-lg bg-brand-shade' : 'hover:bg-brand-bg'].join(' ')}
                    >
                      <span className="text-sm font-semibold text-ink">{u.name}</span>
                      <span className="text-xs text-ink-secondary">{u.code ? `Code ${u.code}` : 'No code'}</span>
                    </button>
                  ))}
                {!isLoading && units.length === 0 && (
                  <span className="border-t border-[#f3f4f6] pt-2.5 text-[13px] text-ink-secondary">
                    No units yet. Add one, or bulk upload.
                  </span>
                )}
              </>
            ) : (
              <>
                {/* Inherited: the level's source nodes (e.g. ECI districts) are selectable — picking
                    one maps it like an Own unit. Anchored: the same nodes, read-only. */}
                {levelNodesLoading && (
                  <span className="border-t border-[#f3f4f6] pt-2.5 text-[13px] text-ink-secondary">Loading nodes…</span>
                )}
                {!levelNodesLoading &&
                  sourceNodes.map((n) => {
                    const rowClass =
                      'flex flex-col items-start gap-0.5 border-t border-[#f3f4f6] px-2 py-2.5 first:border-t-0';
                    const inner = (
                      <>
                        <span className="text-sm font-semibold text-ink">{n.node_name}</span>
                        <span className="text-xs text-ink-secondary">
                          {n.node_code ? `Code ${n.node_code}` : 'No code'}
                          {n.node_level ? ` · ${n.node_level}` : ''}
                        </span>
                      </>
                    );
                    return mapSource ? (
                      <button
                        key={n.node_code || n.node_name}
                        type="button"
                        onClick={() => void selectSourceNode(n)}
                        className={[rowClass, 'text-left transition-colors', n.node_code === selNodeCode ? 'rounded-lg bg-brand-shade' : 'hover:bg-brand-bg'].join(' ')}
                      >
                        {inner}
                      </button>
                    ) : (
                      <div key={n.node_code || n.node_name} className={rowClass}>
                        {inner}
                      </div>
                    );
                  })}
                {!levelNodesLoading && sourceNodes.length === 0 && (
                  <span className="border-t border-[#f3f4f6] pt-2.5 text-[13px] text-ink-secondary">
                    No source nodes found for {levelName}.
                  </span>
                )}
              </>
            )}
          </div>
        </section>

        {/* Member chooser */}
        <section className="min-w-0 flex-1 rounded-xl border border-line/30 bg-white p-5 shadow-card">
          {!canMapMembers ? (
            <p className="text-sm text-ink-secondary">
              These units come from {levelNodes?.source || 'the mapped source'}
              {levelNodes?.source_level ? ` › ${levelNodes.source_level}` : ''} and are read-only —
              they’re listed on the left. Nothing to map here.
            </p>
          ) : !sel ? (
            <p className="text-sm text-ink-secondary">
              {mapSource
                ? `Select a ${levelName.toLowerCase()} on the left to map its members.`
                : 'Add a unit, or select one to map its members.'}
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <label className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="text-xs font-semibold text-ink-secondary">Unit name</span>
                  {/* Only Own levels can rename/delete units; anchored/inherited ones come from the source. */}
                  <Input
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    onBlur={saveName}
                    size="large"
                    readOnly={!isOwnLevel}
                  />
                </label>
                <button
                  type="button"
                  onClick={removeSel}
                  disabled={!isOwnLevel}
                  title={
                    !isOwnLevel
                      ? 'Inherited units come from the source — they can only be mapped, not renamed or deleted.'
                      : undefined
                  }
                  className="mt-[22px] h-11 shrink-0 rounded-lg border border-[#fecaca] bg-white px-3.5 text-[13px] font-semibold text-[#dc2626] transition-colors hover:bg-[#fef2f2] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Delete unit
                </button>
              </div>

              {!canMap ? (
                <div className="rounded-lg border border-line/50 bg-[#f9fafb] p-3.5 text-[13px] text-ink-secondary">
                  This level isn’t composed of a lower source (the level below is not anchored/inherited),
                  so there are no nodes to map.
                </div>
              ) : (
                <>
                  {/* Members (selected) — shown as removable chips */}
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-bold text-ink">Members</span>
                      <span className="text-xs text-ink-secondary">
                        {selectedCount} {plural(lowerName).toLowerCase()}
                        {lowerSourceLabel ? ` · ${lowerSourceLabel}` : ''}
                      </span>
                    </div>
                    {selectedCount === 0 ? (
                      <span className="text-[13px] text-ink-secondary">
                        No members yet. Tick {plural(lowerName).toLowerCase()} below to add them.
                      </span>
                    ) : (
                      <>
                        <p className="text-[13px] text-ink-secondary">
                          Spans {selectedCount} {selectedCount === 1 ? lowerName.toLowerCase() : plural(lowerName).toLowerCase()}:{' '}
                          <span className="font-semibold text-ink">{selectedList.map((n) => n.node_name).join(', ')}</span>
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {selectedList.map((n) => (
                            <span
                              key={n.node_code}
                              className="inline-flex items-center gap-1.5 rounded-full border border-brand/30 bg-brand-shade py-1 pl-3 pr-1.5 text-[13px] font-semibold text-brand"
                            >
                              {n.node_name}
                              <button
                                type="button"
                                onClick={() => toggleNode(n)}
                                title={`Remove ${n.node_name}`}
                                className="flex size-4 items-center justify-center rounded-full text-[15px] leading-none text-brand transition-colors hover:bg-brand/15"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Add members — search + checkable node grid */}
                  <div className="flex flex-col gap-2">
                    <span className="text-sm font-bold text-ink">Add members</span>
                    <div className="flex flex-wrap items-center gap-3">
                      <Input
                        className="min-w-[220px] flex-1"
                        value={nodeSearch}
                        onChange={(e) => setNodeSearch(e.target.value)}
                        placeholder={`Search ${lowerName.toLowerCase()} by name or code`}
                        size="large"
                        allowClear
                      />
                      <label className="flex shrink-0 items-center gap-2 text-[13px] text-ink">
                        <Checkbox checked={onlyFree} onChange={(e) => setOnlyFree(e.target.checked)} />
                        Only show ones not in another unit
                      </label>
                    </div>

                    <div className="flex flex-col overflow-hidden rounded-lg border border-line/50">
                      <div className="flex items-center justify-between gap-2 border-b border-[#f3f4f6] bg-[#f9fafb] px-3 py-2">
                        <span className="text-xs font-semibold text-ink-secondary">
                          {lowerSourceLabel ? `${lowerSourceLabel} › ` : ''}{lowerName} · {availNodes.length} {availNodes.length === 1 ? 'node' : 'nodes'}
                        </span>
                        <label className="flex items-center gap-2 text-[13px] text-ink">
                          <Checkbox
                            checked={allShownOn}
                            indeterminate={!allShownOn && availNodes.some((n) => selectedNodes[n.node_code])}
                            onChange={toggleAllShown}
                            disabled={availNodes.length === 0}
                          />
                          Select all shown
                        </label>
                      </div>
                      <div className="max-h-[520px] overflow-y-auto p-2">
                        {nodesLoading && <span className="block p-2 text-[13px] text-ink-secondary">Loading nodes…</span>}
                        {!nodesLoading && availNodes.length === 0 && (
                          <span className="block p-2 text-[13px] text-ink-secondary">
                            {nodeSearchQ
                              ? 'No nodes match your search.'
                              : `No ${lowerName.toLowerCase()} nodes available yet.`}
                          </span>
                        )}
                        {availNodes.length > 0 && (
                          <div className="grid grid-cols-1 gap-x-4 gap-y-0.5 sm:grid-cols-2 xl:grid-cols-3">
                            {availNodes.map((n) => {
                              const on = Boolean(selectedNodes[n.node_code]);
                              return (
                                <label
                                  key={n.node_code}
                                  className={['flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-ink transition-colors', on ? 'bg-brand-shade/50' : 'hover:bg-brand-bg'].join(' ')}
                                >
                                  <Checkbox checked={on} onChange={() => toggleNode(n)} />
                                  <span className="truncate font-medium">{n.node_name}</span>
                                  <span className="ml-auto shrink-0 text-[11px] text-ink-secondary">{n.node_code}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Save bar */}
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#f3f4f6] pt-3">
                <span className="text-[13px] text-ink-secondary">
                  {selectedCount} {selectedCount === 1 ? 'node' : 'nodes'} selected
                </span>
                <div className="flex items-center gap-2.5">
                  {saveFlash && <span className="text-[13px] font-semibold text-brand">Saved</span>}
                  <button
                    type="button"
                    onClick={saveUnit}
                    disabled={saving}
                    className="h-10 rounded-lg bg-brand px-5 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {saving ? 'Saving…' : 'Save unit'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
